<script setup lang="ts">
import { computed, ref } from "vue";
import { useToast } from "vue-toastification";
import * as Sentry from "@sentry/vue";
import { format as formatDate } from "date-fns";
import { BookmarkPlus, Loader2 } from "@lucide/vue";

import { useModalStore } from "@/stores/modals/modals.store";
import TextInputNew from "@/components/Reusables/Inputs/TextInputNew.vue";
import { useProjectRevisionsStore } from "../stores/projectRevisions.store";
import type { ProjectVersion } from "../types/projectVersion.types";

// Save-a-version-as-a-revision dialog. Unlike SaveProjectRevisionModal (which saves the current
// state and makes the new revision current), this one creates a revision born frozen holding the
// project AS OF this version: the backend replays the event stream at the version's pin for the
// name, base information, materials, models, energy data and areas, and the mapping families
// (waste, transport, special conditions, standard values) are copied from their row history at
// the version's instant. The current pointer never moves — the team keeps working exactly where
// it is. Admins only (the caller gates the entry button; the endpoint enforces it too).
const props = defineProps<{
  projectId: string;
  version: ProjectVersion;
  projectName: string;
}>();

const toast = useToast();
const { closeByName } = useModalStore();
const revisionsStore = useProjectRevisionsStore();

const revisionName = ref(`${props.projectName} — v${props.version.versionNumber}`);
const isSaving = ref(false);

const nameError = computed(() => (revisionName.value.trim() ? "" : "Give the revision a name"));

const versionDate = computed(() => formatDate(new Date(props.version.createdAt), "MMM d, yyyy 'at' HH:mm"));

const cancel = () => closeByName("SaveRevisionFromVersionModal", { action: "cancel" });

const save = async () => {
  if (nameError.value || isSaving.value) return;
  isSaving.value = true;
  const name = revisionName.value.trim();
  try {
    const revisionId = await revisionsStore.saveRevisionFromVersion(props.projectId, props.version.id, name);
    toast.success(`Revision “${name}” saved from version ${props.version.versionNumber}.`);
    closeByName("SaveRevisionFromVersionModal", { action: "saved", revisionId });
  } catch (e) {
    Sentry.captureException(e);
    toast.error("Could not save the revision. Please try again.");
  } finally {
    isSaving.value = false;
  }
};

const btnBase =
  "focus-ring interactive-active t-small-bold duration-fast ease-standard inline-flex items-center justify-center gap-2 rounded-8 px-4 py-2 transition-colors";
</script>

<template>
  <div class="flex flex-col gap-4">
    <h2 class="t-sh4 text-type-default">Save version {{ version.versionNumber }} as a revision</h2>

    <p class="t-small text-type-muted">
      This keeps version {{ version.versionNumber }} as a permanent named save point — the project as it was at that version,
      including its name, base information, materials, models, energy data and mappings. The project stays on the
      <span class="t-small-bold text-type-default">current</span> revision; you can switch to the new save point any time from the
      Revisions tab.
    </p>

    <!-- What you're saving -->
    <div class="rounded-8 p-px-12 gap-px-4 flex flex-col bg-slate-100 ring-1 ring-slate-200">
      <p class="t-small-bold text-type-default">
        {{ version.summary }}<span v-if="version.entityLabel"> — {{ version.entityLabel }}</span>
      </p>
      <p class="t-caption text-type-muted">{{ versionDate }} · {{ version.createdBy.name }}</p>
    </div>

    <TextInputNew
      v-model="revisionName"
      label="Revision name"
      :error-msg="nameError"
      :disabled="isSaving"
      required />

    <div class="gap-px-8 flex justify-end">
      <button
        type="button"
        :disabled="isSaving"
        :class="[
          btnBase,
          'bg-slate-0 text-type-default border border-slate-200 hover:bg-slate-100',
          { 'interactive-disabled': isSaving },
        ]"
        data-cy="save-from-version-cancel"
        @click="cancel">
        Cancel
      </button>
      <button
        type="button"
        :disabled="!!nameError || isSaving"
        :class="[
          btnBase,
          !nameError && !isSaving
            ? 'bg-app-blue-600 dark:bg-app-blue-fill hover:bg-app-blue-700 dark:hover:bg-app-blue-fill-hover text-white'
            : 'text-type-muted bg-slate-200',
          { 'interactive-disabled': !!nameError || isSaving },
        ]"
        data-cy="save-from-version-confirm"
        @click="save">
        <Loader2
          v-if="isSaving"
          class="icon-sm animate-spin"
          :stroke-width="1.5" />
        <BookmarkPlus
          v-else
          class="icon-sm"
          :stroke-width="1.5" />
        {{ isSaving ? "Saving…" : "Save revision" }}
      </button>
    </div>
  </div>
</template>
